import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { validateToken } from "../services/auth-service";
import { getRouteForDestination, ROUTES } from "../config/routes";

const AuthenticatePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const authenticateToken = async () => {
      try {
        // Extract URL parameters
        const token = searchParams.get("token");
        const consentId = searchParams.get("CID");
        const collectionPointId = searchParams.get("CPID");
        const assignmentId = searchParams.get("AID");
        const type = searchParams.get("type");
        const identifier = searchParams.get("identifier");
        const destination = searchParams.get("destination");

        if (!token) {
          navigate(ROUTES.LOGIN, {
            state: {
              error: "Invalid access link. Missing token.",
              errorType: "INVALID_LINK",
              context: { type, identifier },
            },
          });
          return;
        }

        // Validate that either consent flow params OR notice flow param is provided
        const isConsentFlow = consentId && collectionPointId;
        const isNoticeFlow = assignmentId;

        if (!isConsentFlow && !isNoticeFlow) {
          navigate(ROUTES.LOGIN, {
            state: {
              error: "Invalid access link. Missing required parameters.",
              errorType: "INVALID_LINK",
              context: { type, identifier },
            },
          });
          return;
        }

        // Clear localStorage before validating new token
        localStorage.removeItem("portal_jwt");

        // Debug: Log what we're about to send
        console.log("AuthenticatePage - About to validate token:", {
          token: token?.substring(0, 10) + "...",
          consentId,
          collectionPointId,
          assignmentId,
          apiBaseUrl: process.env.REACT_APP_DEV_API_BASE_URL || 'http://localhost:3001'
        });

        // Always validate the new token
        const response = await validateToken(
          token,
          consentId ? parseInt(consentId) : undefined,
          collectionPointId ? parseInt(collectionPointId) : undefined,
          assignmentId ? parseInt(assignmentId) : undefined
        );

        // Debug logging
        console.log("AuthenticatePage - Token validation response:", {
          success: response.success,
          clientData: response.clientData,
          clientId: response.clientData?.clientId,
        });

        if (response.success && response.jwt && response.clientData) {
          // Store JWT in localStorage (single key-value pair)
          localStorage.setItem("portal_jwt", response.jwt);

          // Redirect based on destination using centralized mapping
          let targetRoute = getRouteForDestination(destination);
          
          // For notice flow, redirect to notices page with assignmentId in state
          if (isNoticeFlow && assignmentId) {
            targetRoute = ROUTES.NOTICES;
          }
          
          // Debug logging for navigation
          const navigationContext = { 
            type, 
            identifier, 
            collectionPointId: collectionPointId || undefined,
            assignmentId: assignmentId || undefined,
            destination, 
            clientId: response.clientData.clientId 
          };
          console.log("AuthenticatePage - Navigating with context:", navigationContext);

          navigate(targetRoute, {
            state: {
              jwt: response.jwt,
              clientData: response.clientData,
              context: navigationContext,
              assignmentId: isNoticeFlow ? parseInt(assignmentId!) : undefined, // Pass assignmentId for notice flow
            },
          });
        } else {
          // Token validation failed - redirect to login with error details and context
          // Extract clientId from error response if available
          const errorClientId = response.clientId || 0;
          console.log("AuthenticatePage - Token validation failed, clientId from error:", errorClientId);

          navigate(ROUTES.LOGIN, {
            state: {
              error: response.error || "Token validation failed",
              errorType: response.errorType || "UNKNOWN_ERROR",
              context: { type, identifier, collectionPointId, clientId: errorClientId },
            },
          });
        }
      } catch (error) {
        console.error("Authentication failed:", error);
        navigate(ROUTES.LOGIN, {
          state: {
            error: "Authentication failed",
            errorType: "AUTHENTICATION_ERROR",
            context: {
              type: searchParams.get("type"),
              identifier: searchParams.get("identifier"),
              collectionPointId: searchParams.get("CPID"),
              clientId: null, // No clientId available in catch block
            },
          },
        });
      } finally {
        setLoading(false);
      }
    };

    authenticateToken();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Authenticating...</Typography>
            <Typography variant="body2" color="text.secondary">
              Validating your access token
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // This should rarely be shown as we redirect on both success and error
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Typography variant="h6">Authentication completed</Typography>
    </Box>
  );
};

export default AuthenticatePage;
